import React from 'react';
import { Box, Stepper, Step, StepLabel } from '@mui/material';
import { FormTheme, StepperVariant, StepperPosition } from '../../types';
import { groupStepsIntoSections, sectionFill } from './stepSections';

/**
 * The step indicator for multi-step forms, in every style the theme offers.
 *
 * Lives in one place because it is rendered on three surfaces — the builder's
 * preview modal, the live form, and the theme editor's own preview — and a
 * style that only landed on some of them would be worse than no choice at all.
 */

export const STEPPER_DEFAULT_ACTIVE = '#4f46e5';
export const STEPPER_DEFAULT_INACTIVE = '#c7c7d1';
export const STEPPER_DEFAULT_LABEL = '#6b7280';

interface FormStepperProps {
  steps: string[];
  /** Zero-based index of the step being shown. */
  activeStep: number;
  theme?: FormTheme | null;
  /** Set on the theme editor's preview, which renders at a reduced size. */
  compact?: boolean;
}

export const getStepperVariant = (theme?: FormTheme | null): StepperVariant =>
  theme?.stepperVariant || 'numbered';

export const getStepperPosition = (theme?: FormTheme | null): StepperPosition =>
  theme?.stepperPosition || 'top';

/** Left-positioned steppers stack beside the form rather than above it. */
export const isStepperVertical = (theme?: FormTheme | null): boolean =>
  getStepperPosition(theme) === 'left';

export const FormStepper: React.FC<FormStepperProps> = ({
  steps,
  activeStep,
  theme,
  compact = false,
}) => {
  if (!steps.length) return null;

  const variant = getStepperVariant(theme);
  const vertical = isStepperVertical(theme);
  const hideLabels = Boolean(theme?.stepperHideLabels);

  const activeColor = theme?.stepperActiveColor || STEPPER_DEFAULT_ACTIVE;
  const inactiveColor = theme?.stepperInactiveColor || STEPPER_DEFAULT_INACTIVE;
  const completedColor = theme?.stepperCompletedColor || activeColor;
  const labelColor = theme?.stepperLabelColor || STEPPER_DEFAULT_LABEL;
  const labelFont =
    theme?.paragraphFontFamily || theme?.fontFamily || 'Arial, sans-serif';

  /** Marker colour for a step, by its position relative to the active one. */
  const colorFor = (index: number) =>
    index === activeStep
      ? activeColor
      : index < activeStep
        ? completedColor
        : inactiveColor;

  const scale = compact ? 0.8 : 1;

  /* ── Numbered circles ─────────────────────────────────────────────── */
  if (variant === 'numbered') {
    return (
      <Stepper
        activeStep={activeStep}
        alternativeLabel={!vertical}
        orientation={vertical ? 'vertical' : 'horizontal'}
        sx={{
          '& .MuiStepConnector-line': {
            borderColor: inactiveColor,
          },
          '& .MuiStepIcon-root': {
            color: inactiveColor,
            fontSize: 26 * scale,
          },
          '& .MuiStepIcon-root.Mui-active': { color: activeColor },
          '& .MuiStepIcon-root.Mui-completed': { color: completedColor },
          '& .MuiStepLabel-label': {
            color: `${labelColor} !important`,
            fontFamily: labelFont,
            fontSize: 13 * scale,
          },
          '& .MuiStepLabel-label.Mui-active': {
            color: `${activeColor} !important`,
            fontWeight: 600,
          },
        }}
      >
        {steps.map((label, index) => (
          <Step key={index}>
            <StepLabel>{hideLabels ? '' : label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    );
  }

  /* ── Progress bar ─────────────────────────────────────────────────── */
  if (variant === 'progress') {
    // Full width on the last step rather than (n-1)/n, so finishing reads as
    // complete instead of stalling just short of the end.
    const percent =
      steps.length <= 1 ? 100 : ((activeStep + 1) / steps.length) * 100;

    return (
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 1,
            fontFamily: labelFont,
            fontSize: 13 * scale,
            color: labelColor,
          }}
        >
          {!hideLabels && (
            <Box component="span" sx={{ fontWeight: 600, color: activeColor }}>
              {steps[activeStep] || ''}
            </Box>
          )}
          <Box component="span">
            Step {activeStep + 1} of {steps.length}
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: 6 * scale,
            borderRadius: 999,
            backgroundColor: inactiveColor,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${percent}%`,
              height: '100%',
              borderRadius: 999,
              backgroundColor: activeColor,
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
      </Box>
    );
  }

  /* ── Dots ─────────────────────────────────────────────────────────── */
  if (variant === 'dots') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: vertical ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.25,
        }}
      >
        {steps.map((label, index) => {
          const isActive = index === activeStep;
          return (
            <Box
              key={index}
              title={label}
              sx={{
                // The active dot is wider, not just darker, so the position is
                // readable without relying on colour alone.
                width: (isActive && !vertical ? 22 : 9) * scale,
                height: 9 * scale,
                borderRadius: 999,
                backgroundColor: colorFor(index),
                transition: 'width 0.25s ease, background-color 0.25s ease',
              }}
            />
          );
        })}
        {!hideLabels && !vertical && (
          <Box
            component="span"
            sx={{
              ml: 1,
              fontFamily: labelFont,
              fontSize: 13 * scale,
              color: labelColor,
            }}
          >
            {steps[activeStep] || ''}
          </Box>
        )}
      </Box>
    );
  }

  /* ── Section bars ─────────────────────────────────────────────────── */
  if (variant === 'sections') {
    const sections = groupStepsIntoSections(steps);
    const activeSection = sections.find(
      (section) => activeStep >= section.start && activeStep < section.start + section.count,
    );

    return (
      <Box sx={{ width: '100%' }}>
        {!hideLabels && activeSection?.label && (
          <Box
            sx={{
              mb: 1,
              textAlign: 'center',
              fontFamily: labelFont,
              fontSize: 14 * scale,
              fontWeight: 600,
              color: labelColor,
            }}
          >
            {activeSection.label}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 0.75, width: '100%' }}>
          {sections.map((section) => (
            <Box
              key={section.start}
              title={section.label}
              sx={{
                flex: 1,
                height: 4 * scale,
                borderRadius: 999,
                backgroundColor: inactiveColor,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${sectionFill(section, activeStep)}%`,
                  height: '100%',
                  borderRadius: 999,
                  backgroundColor: activeColor,
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  /* ── Tabs / segments ──────────────────────────────────────────────── */
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        gap: 1,
        width: '100%',
      }}
    >
      {steps.map((label, index) => {
        const isActive = index === activeStep;
        const isDone = index < activeStep;
        return (
          <Box
            key={index}
            sx={{
              flex: vertical ? '0 0 auto' : 1,
              minWidth: 0,
              px: 1.5,
              py: 0.9,
              borderRadius: `${theme?.borderRadius ?? 8}px`,
              textAlign: 'center',
              backgroundColor: isActive
                ? activeColor
                : isDone
                  ? `${completedColor}22`
                  : 'transparent',
              border: `1px solid ${isActive ? activeColor : inactiveColor}`,
              color: isActive ? '#fff' : isDone ? completedColor : labelColor,
              fontFamily: labelFont,
              fontSize: 13 * scale,
              fontWeight: isActive ? 600 : 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              transition: 'background-color 0.2s ease, color 0.2s ease',
            }}
          >
            {hideLabels ? index + 1 : label}
          </Box>
        );
      })}
    </Box>
  );
};

export default FormStepper;
